import {
    type FC,
    useState,
    useCallback,
    type ComponentProps,
    type ReactNode,
    type ChangeEvent,
} from 'react';
import { graphql, useFragment } from 'react-relay';
import { constants } from 'ethers';
import { Input } from 'dibs-elements/exports/Input.tsx';
import { useCurrencyStore } from 'dibs-buyer-layout/exports/currencyStore.ts';
import { useEtherConversion } from 'dibs-crypto-utils/exports/useEtherConversion.ts';

import styles from './EthereumAmountInput.scss';

import { type EthereumAmountInput_etherRates$key } from './__generated__/EthereumAmountInput_etherRates.graphql.ts';

// match up to 18 decimal places
const bidRegex = /^\d*\.?\d{0,18}$/;

type InputProps = ComponentProps<typeof Input>;

type Props = {
    etherRates: EthereumAmountInput_etherRates$key | null | undefined;
    value: InputProps['value'];
    onChange: InputProps['onChange'];
    onBlur?: InputProps['onBlur'];
    title: InputProps['title'];
    errorMessage?: InputProps['errorMessage'];
    disabled?: InputProps['disabled'];
    minValue?: string | number;
    autoFocus?: InputProps['autoFocus'];
    label?: ReactNode;
};

const ratesFragment = graphql`
    fragment EthereumAmountInput_etherRates on EtherRates @relay(plural: true) {
        ...useEtherConversion_etherRates
    }
`;

export const EthereumAmountInput: FC<Props> = ({
    value,
    etherRates: etherRatesRef,
    onChange,
    onBlur,
    errorMessage,
    disabled,
    minValue,
    title,
    autoFocus,
    label,
}) => {
    const { currency } = useCurrencyStore();
    const etherRates = useFragment(ratesFragment, etherRatesRef);

    const { getConvertedEther, getAmountStrings } = useEtherConversion({
        currency,
        etherRates,
    });

    const { converted: convertedAmount } = getAmountStrings(value?.toString() ?? `${minValue}`);
    const [convertedAmountDecorator, setConvertedAmountDecorator] = useState(convertedAmount);

    const onBidInput = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            if (bidRegex.test(e.target.value)) {
                const convertedBid = ['.', ''].includes(e.target.value)
                    ? getConvertedEther(`${minValue ?? 0}`)
                    : getConvertedEther(e.target.value);
                setConvertedAmountDecorator(convertedBid);
                onChange?.(e);
            }
        },
        [onChange, getConvertedEther, minValue]
    );

    return (
        <Input
            dataTn="ethereum-amount-input"
            size="large"
            inputMode="decimal"
            autoFocus={autoFocus}
            pattern={bidRegex.toString()}
            title={title}
            min={minValue}
            placeholder={minValue?.toString()}
            rightDecorator={convertedAmountDecorator}
            rightDecoratorClass={styles.conversionDecorator}
            leftDecorator={constants.EtherSymbol}
            leftDecoratorClass={styles.symbolDecorator}
            useCircularErrorIcon
            horizontalSpacing="large"
            onChange={onBidInput}
            onBlur={onBlur}
            value={value}
            errorMessage={errorMessage}
            hasError={!!errorMessage}
            disabled={disabled}
            label={label}
        />
    );
};
